import { CLIENT_LOCALE, formatClientDate, formatClientDateTimeMedium } from '@/lib/client-locale'

const SECOND = 1000
const MINUTE = 60 * SECOND
const HOUR = 60 * MINUTE
const DAY = 24 * HOUR
const WEEK = 7 * DAY
const MONTH = 30 * DAY
const YEAR = 365 * DAY

const rtf = new Intl.RelativeTimeFormat(CLIENT_LOCALE, { numeric: 'auto' })

export function toDate(input: string | number | Date): Date {
  if (input instanceof Date) return input
  return new Date(input)
}

/**
 * Friendly relative-time string. Bands:
 * - <60s "just now"
 * - <60m N minutes ago
 * - <24h N hours ago
 * - <7d "yesterday" / N days ago
 * - <30d N weeks ago
 * - <12m N months ago
 * - else "Mon D" or "Mon D, YYYY" if not current year
 */
export function formatRelativeTime(input: string | number | Date): string {
  const date = toDate(input)
  if (Number.isNaN(date.getTime())) return ''

  const now = Date.now()
  const diffMs = date.getTime() - now
  const absMs = Math.abs(diffMs)
  const sign = diffMs >= 0 ? 1 : -1

  if (absMs < MINUTE) return 'just now'
  if (absMs < HOUR) return rtf.format(sign * Math.round(absMs / MINUTE), 'minute')
  if (absMs < DAY) return rtf.format(sign * Math.round(absMs / HOUR), 'hour')
  if (absMs < WEEK) return rtf.format(sign * Math.round(absMs / DAY), 'day')
  if (absMs < MONTH) return rtf.format(sign * Math.round(absMs / WEEK), 'week')
  if (absMs < YEAR) return rtf.format(sign * Math.round(absMs / MONTH), 'month')

  const sameYear = date.getFullYear() === new Date().getFullYear()
  return formatClientDate(input, {
    month: 'short',
    day: 'numeric',
    year: sameYear ? undefined : 'numeric',
  })
}

/**
 * Relative time using calendar-day bands only (today, yesterday, N days ago).
 * For job "Posted" lines and other copy that should not use hours/weeks/months.
 */
export function formatRelativeTimeInDays(input: string | number | Date): string {
  const date = toDate(input)
  if (Number.isNaN(date.getTime())) return ''

  const now = new Date()
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime()
  const dateStart = new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime()
  const diffDays = Math.round((todayStart - dateStart) / DAY)

  if (diffDays === 0) return 'today'
  if (diffDays === 1) return 'yesterday'
  return rtf.format(-diffDays, 'day')
}

export function formatAbsoluteDateTime(input: string | number | Date): string {
  const date = toDate(input)
  if (Number.isNaN(date.getTime())) return ''
  return formatClientDateTimeMedium(input)
}
